// Quiz difficulty stats, built from our own leaderboard.
//
// This is the one page on the site that publishes something no wiki can copy:
// what real players actually score. Numbers are baked in at build time by
// scripts/build-quiz-stats.js so they exist in the HTML for crawlers that don't
// run JavaScript (OAI-SearchBot, which is how ChatGPT reads the web).

import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import quizStats from '../data/quizStats';
import './QuizStats.css';

const pct = (n) => `${n}%`;

const QuizStats = () => {
  const { games, generated } = quizStats;
  const byKey = Object.fromEntries(games.map((g) => [g.key, g]));
  // Which game is toughest skips anything under minSample plays — Bounty Battle
  // has the highest average on the site off two recorded rounds, which says
  // nothing. We name the toughest game and stop there: calling one of our own
  // games "the easiest" only tells people not to bother with it.
  const hardest = byKey[quizStats.hardestKey];
  const thin = games.filter((g) => !g.reliable);

  // Front-loaded answer — the first thing on the page and the first thing an
  // extraction model sees. Deliberately no play or player counts: the
  // leaderboard lost its early history to a wipe, so any total we printed would
  // understate reality and read as a smaller site than this is.
  const summary =
    `The ${hardest.label} is the toughest of the four: players average ` +
    `${hardest.average} out of ${hardest.outOf} and nobody has scored a perfect round yet. ` +
    `Every game has its own scoreline, and they are all listed below.`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Dataset',
    name: 'One Piece Trivia — quiz difficulty results',
    description: summary,
    url: 'https://www.onepiecetrivia.com/quiz-stats',
    creator: { '@type': 'Organization', name: 'One Piece Trivia' },
    // No temporalCoverage on purpose: the leaderboard's early history was lost to
    // a wipe, so the earliest surviving score would advertise a start date years
    // later than the site actually began.
    dateModified: generated,
    isAccessibleForFree: true,
    variableMeasured: games.map((g) => ({
      '@type': 'PropertyValue',
      name: `${g.label} average score`,
      value: g.average,
      maxValue: g.outOf,
    })),
  };

  return (
    <>
      <Helmet>
        <title>How Hard Is the One Piece Quiz? Real Player Scores | One Piece Trivia</title>
        <meta
          name="description"
          content={`What real players score on the One Piece quizzes: averages, best-ever scores and perfect rounds for all four games. ${hardest.label} average ${hardest.average}/${hardest.outOf}.`}
        />
        <link rel="canonical" href="https://www.onepiecetrivia.com/quiz-stats" />
        <meta property="og:title" content="How Hard Is the One Piece Quiz?" />
        <meta property="og:description" content="What players really score, taken from the leaderboard rather than guessed." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/quiz-stats" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="qs-container">
        <header className="qs-header">
          <h1 className="qs-title">How Hard Is the One Piece Quiz?</h1>
          <p className="qs-lead">{summary}</p>
          <p className="qs-meta">
            Measured from scores players saved to our leaderboard. Updated {generated}.
          </p>
        </header>

        <section className="qs-section">
          <h2 className="qs-h2">How players score, game by game</h2>
          <div className="qs-table-scroll">
            <table className="qs-table">
              <thead>
                <tr>
                  <th>Quiz</th>
                  <th>Average score</th>
                  <th>Typical score</th>
                  <th>Best ever</th>
                  <th>Perfect rounds</th>
                </tr>
              </thead>
              <tbody>
                {games.map((g) => (
                  <tr key={g.key}>
                    <th scope="row">
                      <Link to={g.path}>{g.label}</Link>
                      {!g.reliable && <span className="qs-thin" title="Too few games to draw conclusions from"> *</span>}
                    </th>
                    <td>
                      {g.average} / {g.outOf}
                    </td>
                    <td>
                      {g.median} / {g.outOf}
                    </td>
                    <td>
                      {g.best} / {g.outOf}
                    </td>
                    <td>{pct(g.perfectRate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {thin.length > 0 && (
            <p className="qs-footnote">
              * {thin.map((g) => g.label).join(' and ')} {thin.length === 1 ? 'is' : 'are'} newer than
              the rest and {thin.length === 1 ? 'has' : 'have'} far fewer saved rounds behind{' '}
              {thin.length === 1 ? 'it' : 'them'}, so {thin.length === 1 ? 'its' : 'their'} figures are
              shown for completeness but are not used to compare the games.
            </p>
          )}
        </section>

        <section className="qs-section">
          <h2 className="qs-h2">What the numbers say</h2>
          <div className="qs-cards">
            {games.map((g) => (
              <article className="qs-card" key={g.key}>
                <h3 className="qs-card-title">{g.label}</h3>
                <div className="qs-bar" aria-hidden="true">
                  <span className="qs-bar-fill" style={{ width: pct(g.difficulty) }} />
                </div>
                <p className="qs-card-stat">
                  Players average <strong>{g.average}</strong> out of {g.outOf} — {pct(g.difficulty)} of
                  a full score.
                </p>
                <p className="qs-card-note">
                  {g.perfect === 0
                    ? `Nobody has scored a perfect round yet. The best anyone has managed is ${g.best} out of ${g.outOf}.`
                    : `${pct(g.perfectRate)} of rounds end in a perfect ${g.outOf} out of ${g.outOf}.`}
                </p>
                <Link className="qs-play" to={g.path}>
                  Play the {g.label} →
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="qs-section qs-prose">
          <h2 className="qs-h2">How we measured this</h2>
          <p>
            Every figure here comes from scores players saved to our own{' '}
            <Link to="/leaderboard">leaderboard</Link> — no estimates and no survey. Rounds that were
            opened and abandoned without answering are left out, since a walkaway is not a wrong
            answer. The page is regenerated from live results every time the site is deployed, so
            these numbers move as people play.
          </p>
        </section>

        <section className="qs-section qs-links">
          <h2 className="qs-h2">Try them yourself</h2>
          <ul className="qs-link-list">
            <li>
              <Link to="/quiz">One Piece Character Quiz</Link> — name the character from their profile
            </li>
            <li>
              <Link to="/image-quiz">One Piece Image Quiz</Link> — name the character from the picture
            </li>
            <li>
              <Link to="/devil-fruit-quiz">Devil Fruit Quiz</Link> — match the fruit to its power
            </li>
            <li>
              <Link to="/bounty-battle">Bounty Battle</Link> — guess whose bounty is higher
            </li>
            <li>
              <Link to="/which-character">Which One Piece Character Are You?</Link> — personality quiz
            </li>
            <li>
              <Link to="/bounties">Bounty Rankings</Link> — every known bounty, highest to lowest
            </li>
            <li>
              <Link to="/fruits">Devil Fruit Directory</Link> — every fruit by type and user
            </li>
            <li>
              <Link to="/characters">Character Archive</Link> — profiles for the whole cast
            </li>
          </ul>
        </section>
      </div>
    </>
  );
};

export default QuizStats;
